import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';

import { StudentScreenTime } from './models/student-screen-time.model';

import { ScreenTimeLimits } from './models/screen-time-limits.model';
import { ParentsService } from 'src/app/shared/services/parents.service';

import { StudentsService } from 'src/app/shared/services/students.service';

import { IStudentResume } from 'src/app/shared/components/student-list-widget/models/IStudentResume';

import { Chart, ChartConfiguration, ChartData, ChartType } from 'chart.js';

import annotationPlugin from 'chartjs-plugin-annotation';
import { ISchoolInfo } from 'src/app/shared/components/student-list-widget/models/ISchoolInfo';

Chart.register(annotationPlugin);


/**
 * Displays the total value above each stacked bar.
 *
 * Grade 2-5:
 *   in school + out of school
 *
 * Grade 6+:
 *   previous cumulative + in school + out of school
 */
const dailyTotalLabelPlugin = {

  id: 'dailyTotalLabelPlugin',

  afterDatasetsDraw(chart: any) {

    const ctx = chart.ctx;

    // Last dataset is the top of the stacked bar.
    const topDatasetIndex = chart.data.datasets.length - 1;

    const meta = chart.getDatasetMeta(topDatasetIndex);

    ctx.save();

    ctx.font = 'bold 11px Arial';

    ctx.fillStyle = '#000000';

    ctx.textAlign = 'center';

    ctx.textBaseline = 'bottom';

    meta.data.forEach((bar: any, index: number) => {

      const total = chart.data.datasets.reduce(
        (sum: number, dataset: any) =>
          sum + (Number(dataset.data[index]) || 0),
        0
      );

      if (total > 0) {

        ctx.fillText(
          total.toString(),
          bar.x,
          bar.y - 6
        );

      }

    });

    ctx.restore();
  }
};

const cumulativeLabelPlugin = {

  id: 'cumulativeLabelPlugin',

  afterDatasetsDraw(chart: any) {

    const ctx = chart.ctx;

    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      ctx.save();
      if (dataset.label === 'Weekly cumulative') {
        ctx.font = 'bold 11px Arial';
      }
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = dataset.label === 'In school' || dataset.label === 'Weekly cumulative'
        ? '#FFFFFF'
        : 'black';

      meta.data.forEach((bar: any, index: number) => {
        const value = dataset.data[index];
        if (value === 0 || isNaN(value)) {
          return;
        }

        ctx.fillText(`+${value}`, bar.x, (bar.base + bar.y) / 2);
      });

      ctx.restore();
    });
  }

};


@Component({

  selector: 'app-student-screen-time',

  templateUrl: './student-screen-time.component.html',

  styleUrls: ['./student-screen-time.component.scss']

})

export class StudentScreenTimeComponent implements OnInit, OnDestroy {

  studentId: string = null;

  gradeCode: string = null;

  student: IStudentResume = null;

  schoolInfo: ISchoolInfo = null;

  gradeDescription = '';


  screenTimeData: StudentScreenTime[] = [];

  screenTimeLimit: ScreenTimeLimits | null = null;


  currentWeek = '';

  private printFilenamePrefix = '';
  private originalPrintTitle: string | null = null;


  dailyLimit: number | null = null;

  weeklyLimit: number | null = null;


  weeklyInSchool = 0;

  weeklyOutOfSchool = 0;

  weeklyTotal = 0;

  remainingWeeklyMinutes = 0;

  weeklyMinutesOver = 0;


  inSchoolPercentage = 0;

  outSchoolPercentage = 0;

  screenTimeLimitsUrl: string = '';

  reportEnabled: boolean = false;

  reportDisabledMessage: string = '';


  // ==============================
  // Chart
  // ==============================

  barChartLabels: any[] = [];

  barChartData: ChartData<'bar'>['datasets'] = [];

  barChartType: ChartType = 'bar';

  barChartLegend = false;

  barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false, // Allows container CSS to strictly dictate height/width
    devicePixelRatio: window.devicePixelRatio || 1, // Forces rendering to match screen crispness
    plugins: { centerLabelPlugin: false } as any
  };


  barChartPlugins = [
    annotationPlugin,
    dailyTotalLabelPlugin,
    cumulativeLabelPlugin
  ];


  constructor(
    private studentsService: StudentsService,
     private parentsService: ParentsService
  ) {
  }

 private getScreenTimeLimitsUrl(): void {
  

  this.parentsService
    .getFeatureConfigurationByName('student_screen_time_limits_url')
    .subscribe({
      next: (response: any) => {
        
        this.screenTimeLimitsUrl = response?.value || '';
      },
      error: (error) => {        
        this.screenTimeLimitsUrl = '';
      }
    });
}

  private getReportEnabled(): void {
    const configurationName = +this.gradeCode <= 5
      ? 'student_screen_time_elementary_report_enabled'
      : 'student_screen_time_secondary_report_enabled';

    this.parentsService
      .getFeatureConfigurationByName(configurationName)
      .subscribe({
        next: (response: any) => {
          this.reportEnabled = response?.value?.trim().toUpperCase() === 'Y';
          if (this.reportEnabled && this.studentId && +this.gradeCode >= 2 && +this.gradeCode <= 12) {
            this.getScreenTimeLimitsUrl();
            this.studentsService.getStudentSchoolInfo(this.studentId).subscribe((schoolInfo: ISchoolInfo) => {
              this.schoolInfo = schoolInfo;});
            this.setPrintGradeDescription();
            this.getStudentScreenTime();
            this.getScreenTimeLimits();
          } else {
            this.getReportDisabledMessage();
          }
        },
        error: (error) => {
          this.reportEnabled = false;
          this.getReportDisabledMessage();
        }
      });
  }

  private getReportDisabledMessage(): void {
    const configurationName = +this.gradeCode <= 5
      ? 'student_screen_time_elementary_report_disabled_message'
      : 'student_screen_time_report_disabled_message';

    this.parentsService
      .getFeatureConfigurationByName(configurationName)
      .subscribe({
        next: (response: any) => {
          this.reportDisabledMessage = response?.value || '';
        },
        error: (error) => {
          this.reportDisabledMessage = '';
        }
      });
  }

  ngOnInit(): void {

    this.setCurrentWeek();

    this.student = this.studentsService.currentStudent();
    console.log('ADMIN Screen Time currentStudent:', this.student);

    if (this.student) {

      this.studentId = this.student.studentId;

      this.gradeCode = this.student.gradeCode;

      // Wait for the applicable grade group's report feature flag.
      if (+this.gradeCode >= 2 && +this.gradeCode <= 12) {
        this.getReportEnabled();
      }

    }
  }

  private setPrintGradeDescription(): void {
    switch (this.gradeCode) {
      case '1':
        this.gradeDescription = '1st Grader Student @';
        break;
      case '2':
        this.gradeDescription = '2nd Grader Student @';
        break;
      case '3':
        this.gradeDescription = '3rd Grader Student @';
        break;
      default:
        this.gradeDescription = `${this.gradeCode}th Grader Student @`;
    }
  }


  // ==============================
  // Chart Options
  // ==============================

  private initializeChartOptions(): void {

    const annotations: any = {};

    const chartLimit =
      +this.gradeCode >= 6
        ? this.weeklyLimit
        : this.dailyLimit;

    if (chartLimit != null && chartLimit > 0) {

      annotations.limitLine = {
        type: 'line',

        yMin: chartLimit,
        yMax: chartLimit,

        borderColor: '#AD0000',
        borderWidth: 2,
        borderDash: [6, 6],

        label: {
          display: true,

          content:
            +this.gradeCode >= 6
              ? `Weekly limit ${chartLimit} min`
              : `Daily limit ${chartLimit} min`,

          position: 'end'
        }
      };
    }

    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,

      scales: {
        x: {
          stacked: true,
          ticks: {
            color: '#000000'
          }
        },

        y: {
          stacked: true,
          beginAtZero: true,
          grace: '10%',
          ticks: {
            color: '#000000',
            stepSize: 50
          }
        }
      },

      plugins: {
        centerLabelPlugin: false,
        legend: {
          display: false
        },

        tooltip: {
          titleColor: '#0B2E6F',
          bodyColor: '#0B2E6F',
          footerColor: '#0B2E6F',
          backgroundColor: '#FFFFFF'
        },

        annotation: {
          annotations: annotations
        },

        datalabels: {
          display: false
        }
      } as any
    };
  }


  // ==============================
  // Current Week
  // ==============================

 private setCurrentWeek(): void {
  const today = new Date();

  // Sunday of the previous completed week
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - today.getDay() - 7);
  sunday.setHours(0, 0, 0, 0);

  // Saturday of the previous completed week
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric'
  };

  const startDate = sunday.toLocaleDateString('en-US', options);
  const endDate = saturday.toLocaleDateString('en-US', {
    ...options,
    year: 'numeric'
  });

  this.currentWeek = `${startDate} - ${endDate}`;
  this.printFilenamePrefix = `ScreenTime_Report_${startDate.replace(/\s/g, '')}-${saturday.toLocaleDateString('en-US', options).replace(/\s/g, '')}_${saturday.getFullYear()}_`;
}


  @HostListener('window:beforeprint')
  setPrintTitle(): void {
    if (!this.studentId || !this.printFilenamePrefix || this.originalPrintTitle !== null) {
      return;
    }
    this.originalPrintTitle = document.title;
    document.title = this.printFilenamePrefix + this.studentId;
  }

  @HostListener('window:afterprint')
  restorePrintTitle(): void {
    if (this.originalPrintTitle !== null) {
      document.title = this.originalPrintTitle;
      this.originalPrintTitle = null;
    }
  }

  ngOnDestroy(): void {
    this.restorePrintTitle();
  }

  // ==============================
  // Process Screen Time
  // ==============================

  private processScreenTime(
    response: StudentScreenTime[]
  ): void {

    const sunday = this.getSundayOfPreviousWeek();
    const weekData: StudentScreenTime[] = [];

    // Build Sunday-Saturday (7 days)
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);


      const apiRecord = response.find(item => {

        const itemDate = new Date(item.dte);

        return itemDate.getFullYear() === date.getFullYear() &&
          itemDate.getMonth() === date.getMonth() &&
          itemDate.getDate() === date.getDate();

      });


      weekData.push({

        endYear: apiRecord?.endYear || 0,

        dte: date.toISOString(),

        districtId:
          apiRecord?.districtId || this.studentId,

        minutesInSchool:
          apiRecord?.minutesInSchool || 0,

        minutesOutOfSchool:
          apiRecord?.minutesOutOfSchool || 0,

        minutesTotal:
          apiRecord?.minutesTotal || 0,

        weeklyTotal:
          apiRecord?.weeklyTotal || 0

      });

    }


    this.screenTimeData = weekData;


    // ==============================
    // Chart Labels
    // ==============================

    this.barChartLabels = weekData.map(item => {

      const date = new Date(item.dte);


      const day = date.toLocaleDateString(
        'en-US',
        {
          weekday: 'short'
        }
      );


      const monthDay = date.toLocaleDateString(
        'en-US',
        {
          month: 'short',
          day: 'numeric'
        }
      );


      return [
        day,
        monthDay
      ];

    });


    // ==============================
    // Weekly Totals
    // ==============================

    this.weeklyInSchool = weekData.reduce(

      (total, item) =>
        total + (item.minutesInSchool || 0),

      0

    );


    this.weeklyOutOfSchool = weekData.reduce(

      (total, item) =>
        total + (item.minutesOutOfSchool || 0),

      0

    );


    this.weeklyTotal = weekData.reduce(

      (total, item) =>
        total + (item.minutesTotal || 0),

      0

    );


    // ==============================
    // Chart Data
    // ==============================

    if (+this.gradeCode >= 6) {



      this.barChartData = [

        {
          // Sum all previous days, starting from Sunday.
          data: weekData.map((item, index) =>
            weekData
              .slice(0, index)
              .reduce(
                (total, day) => total + (day.minutesTotal || 0),
                0
              )
          ),

          label: 'Weekly cumulative',

          stack: 'screenTime',

          backgroundColor: '#002575',

          borderColor: '#002575',

          borderWidth: 0,

          barThickness: 55,

          borderRadius: (context: any) => {
            const index = context.dataIndex;

            const hasCurrentDayMinutes =
              (weekData[index]?.minutesInSchool || 0) +
              (weekData[index]?.minutesOutOfSchool || 0) > 0;

            return hasCurrentDayMinutes
              ? 0
              : {
                topLeft: 6,
                topRight: 6,
                bottomLeft: 0,
                bottomRight: 0
              };
          },

          borderSkipped: false
        },



        {
          // Current day's in-school minutes.
          data: weekData.map(
            item => item.minutesInSchool || 0
          ),

          label: 'In school',

          stack: 'screenTime',

          backgroundColor: '#0876c9',

          borderColor: '#0876c9',

          borderWidth: 0,

          barThickness: 55,

          borderRadius: (context: any) => {
            const index = context.dataIndex;

            const hasOutOfSchoolMinutes =
              (weekData[index]?.minutesOutOfSchool || 0) > 0;

            return hasOutOfSchoolMinutes
              ? 0
              : {
                topLeft: 6,
                topRight: 6,
                bottomLeft: 0,
                bottomRight: 0
              };
          },

          borderSkipped: false
        },


        {
          // Current day's out-of-school minutes.
          data: weekData.map(
            item => item.minutesOutOfSchool || 0
          ),

          label: 'Out of school',

          stack: 'screenTime',

          backgroundColor: '#83c8ed',

          borderColor: '#83c8ed',

          borderWidth: 0,

          barThickness: 55,

          inflateAmount: 0,

          borderRadius: (context: any) => {
            const value = Number(context.raw) || 0;

            return value > 0
              ? {
                topLeft: 6,
                topRight: 6,
                bottomLeft: 0,
                bottomRight: 0
              }
              : 0;
          },

          borderSkipped: false

        }

      ];

    } else {

      /*
       * Grades 2-5
       *
       * Keep existing behavior:
       *
       * In school
       * +
       * Out of school
       */

      this.barChartData = [

        {
          data: weekData.map(
            item => item.minutesInSchool || 0
          ),

          label: 'In school',

          stack: 'screenTime',

          backgroundColor: '#0876c9',

          borderColor: '#0876c9',

          borderWidth: 0,

          barThickness: 55,

          inflateAmount: 0,

          borderRadius: (context: any) => {
            const index = context.dataIndex;

            const hasOutOfSchoolMinutes =
              (weekData[index]?.minutesOutOfSchool || 0) > 0;

            return hasOutOfSchoolMinutes
              ? 0
              : {
                topLeft: 6,
                topRight: 6,
                bottomLeft: 0,
                bottomRight: 0
              };
          },

          borderSkipped: false
        },


        {
          data: weekData.map(
            item => item.minutesOutOfSchool || 0
          ),

          label: 'Out of school',

          stack: 'screenTime',

          backgroundColor: '#83c8ed',

          borderColor: '#83c8ed',

          borderWidth: 0,

          barThickness: 55,

          inflateAmount: 0,

          borderRadius: (context: any) => {
            const value = Number(context.raw) || 0;

            return value > 0
              ? {
                topLeft: 6,
                topRight: 6,
                bottomLeft: 0,
                bottomRight: 0
              }
              : 0;
          },

          borderSkipped: false

        }

      ];

    }


    this.calculateWeeklyValues();

  }


  // ==============================
  // Sunday of Previous Completed Week
  // ==============================

  private getSundayOfPreviousWeek(): Date {
  const today = new Date();

  const sunday = new Date(today);
  sunday.setDate(today.getDate() - today.getDay() - 7);
  sunday.setHours(0, 0, 0, 0);

  return sunday;
}


  // ==============================
  // Process Limits API
  // ==============================

  private processScreenTimeLimit(
    response: ScreenTimeLimits[]
  ): void {

    if (!response || response.length === 0) {

      this.screenTimeLimit = null;

      this.dailyLimit = null;

      this.weeklyLimit = null;

      return;

    }


    this.screenTimeLimit = response[0];

    this.dailyLimit =
      this.screenTimeLimit.dailyLimit;

    this.weeklyLimit =
      this.screenTimeLimit.weeklyLimit;


    this.calculateWeeklyValues();

    this.initializeChartOptions();  

  }


  // ==============================
  // Weekly Progress
  // ==============================

  private calculateWeeklyValues(): void {

    if (
      this.weeklyLimit == null ||
      this.weeklyLimit <= 0
    ) {

      this.remainingWeeklyMinutes = 0;

      this.weeklyMinutesOver = 0;

      this.inSchoolPercentage = 0;

      this.outSchoolPercentage = 0;

      return;

    }


    this.remainingWeeklyMinutes = Math.max(

      this.weeklyLimit -
      this.weeklyTotal,

      0

    );


    this.weeklyMinutesOver = Math.max(

      this.weeklyTotal -
      this.weeklyLimit,

      0

    );


    this.inSchoolPercentage =
      (this.weeklyInSchool /
        this.weeklyLimit) * 100;


    this.outSchoolPercentage =
      (this.weeklyOutOfSchool /
        this.weeklyLimit) * 100;

  }


  // ==============================
  // Screen Time API
  // ==============================

 private getStudentScreenTime(): void {

  if (!this.studentId) {
    return;
  }

  this.studentsService
    .getStudentScreenTime(this.studentId)
    .subscribe({

     next: (response: StudentScreenTime[]) => {
  

  this.processScreenTime(response || []);
},

      error: (error) => {       

        this.screenTimeData = [];

      }

    });

}


  // ==============================
  // Screen Time Limits API
  // ==============================

  private getScreenTimeLimits(): void {

    if (
      !this.studentId ||
      !this.gradeCode
    ) {

      return;

    }


    this.studentsService
      .getScreenTimeLimits(
        this.studentId,
        this.gradeCode
      )
      .subscribe({

        next: (
          response: ScreenTimeLimits[]
        ) => {

          this.processScreenTimeLimit(
            response
          );

        },


        error: (error) => {

          console.error(
            'Error getting screen time limits',
            error
          );

          this.screenTimeLimit = null;

        }

      });

  } 


  // ==============================
  // Table Limit
  // ==============================

  get tableLimit(): number | null {

    // Grades 6+ compare against weekly limit.
    if (+this.gradeCode >= 6) {

      return this.weeklyLimit;

    }

    // Grades 2-5 compare against daily limit.
    return this.dailyLimit;

  }


  // ==============================
  // Cumulative Total
  // ==============================

  getCumulativeTotal(
    index: number
  ): number {

    return this.screenTimeData

      .slice(
        0,
        index + 1
      )

      .reduce(

        (total, item) =>
          total +
          (item.minutesTotal || 0),

        0

      );

  }

}